import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import type { ErrorComponentProps } from "@tanstack/react-router"
import { Loader2, RotateCw, TriangleAlert } from "lucide-react"

import type { AuthResponseDto } from "@/client/types.gen"
import { Button } from "@/components/ui/button"
import { queryKeys } from "@/lib/queries"

type ReportState =
  | { status: "idle" | "sending" | "failed" }
  | { status: "sent"; ref: string }

// Larsen rejects a description over 10000 characters, a route over 500 and a
// URL over 2000, so every part that varies in length is capped.
const MAX_SUMMARY_LENGTH = 1000
const MAX_STACK_LENGTH = 4000
const MAX_PATH_LENGTH = 500

const DISCLOSURE_ID = "error-report-disclosure"

function clip(text: string, max: number) {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function errorSummary(error: unknown) {
  return clip(
    error instanceof Error ? `${error.name}: ${error.message}` : String(error),
    MAX_SUMMARY_LENGTH
  )
}

function currentPath() {
  return window.location.pathname.slice(0, MAX_PATH_LENGTH)
}

function reportDescription(error: unknown) {
  const stack = error instanceof Error ? (error.stack ?? "") : ""
  return [
    "Automatische Meldung von der Fehlerseite.",
    "",
    `Fehler: ${errorSummary(error)}`,
    `Seite: ${currentPath()}`,
    `Zeit: ${new Date().toISOString()}`,
    "",
    "Stack:",
    clip(stack, MAX_STACK_LENGTH) || "(keiner)",
  ].join("\n")
}

/**
 * Replaces TanStack Router's bare default error view. Reports to Larsen
 * directly through the core client instead of `LarsenProvider`, because a
 * root-level error renders this page without the app shell around it.
 */
export function AppErrorPage({ error }: ErrorComponentProps) {
  const queryClient = useQueryClient()
  const [report, setReport] = useState<ReportState>({ status: "idle" })

  const apiUrl = import.meta.env.VITE_LARSEN_API_URL as string | undefined
  const projectKey = import.meta.env.VITE_LARSEN_PROJECT_KEY as
    | string
    | undefined
  const canReport = Boolean(apiUrl && projectKey)

  async function sendReport() {
    if (!apiUrl || !projectKey) return
    setReport({ status: "sending" })
    const email = queryClient.getQueryData<AuthResponseDto>(queryKeys.me())
      ?.user.email
    try {
      // Loaded on click: the core client brings its own zod copy and the
      // screenshot library, which would otherwise weigh on every cold start.
      const { createFeedbackClient } = await import("@larsen/core")
      const result = await createFeedbackClient({
        apiUrl,
        projectKey,
      }).submitFeedback({
        category: "bug",
        description: reportDescription(error),
        submitterEmail: email,
        metadata: {
          // Search and hash stay out: the sign-in link carries its token there.
          url: `${window.location.origin}${currentPath()}`,
          route: currentPath(),
          viewport: { width: window.innerWidth, height: window.innerHeight },
          userAgent: navigator.userAgent,
          locale: navigator.language,
          capturedAt: new Date().toISOString(),
        },
      })
      setReport({ status: "sent", ref: result.id.slice(0, 8).toUpperCase() })
    } catch {
      setReport({ status: "failed" })
    }
  }

  return (
    <div className="flex min-h-[60svh] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-5" role="alert">
        <TriangleAlert className="size-8 text-primary" aria-hidden />
        <div className="space-y-2">
          <h1 className="font-display text-[22px] font-medium tracking-tight">
            Hier ist etwas schiefgelaufen.
          </h1>
          <p className="text-[15px] leading-relaxed text-muted-foreground">
            Die Seite konnte nicht angezeigt werden. Lade die Anwendung neu, das
            behebt die meisten Fälle.
            {canReport
              ? " Bleibt der Fehler, melde ihn uns bitte mit einem Klick."
              : null}
          </p>
        </div>

        <div className="space-y-2.5">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => window.location.reload()} type="button">
              <RotateCw />
              Anwendung neu laden
            </Button>
            {canReport && report.status !== "sent" ? (
              <Button
                aria-describedby={DISCLOSURE_ID}
                disabled={report.status === "sending"}
                onClick={() => void sendReport()}
                type="button"
                variant="outline"
              >
                {report.status === "sending" ? (
                  <Loader2 className="animate-spin" />
                ) : null}
                Fehler melden
              </Button>
            ) : null}
            <Button asChild type="button" variant="ghost">
              <a href="/">Zur Startseite</a>
            </Button>
          </div>
          {canReport && report.status !== "sent" ? (
            <p
              className="text-[13px] leading-relaxed text-muted-foreground"
              id={DISCLOSURE_ID}
            >
              Gesendet werden die Fehlermeldung mit technischem Verlauf, die
              Seitenadresse ohne Parameter, Zeitpunkt, Browser, Sprache und
              Fenstergröße sowie, falls du angemeldet bist, deine
              E-Mail-Adresse.
            </p>
          ) : null}
        </div>

        {report.status === "sent" ? (
          <p className="text-[14px]" role="status">
            Danke, der Fehler ist gemeldet (Ref {report.ref}).
          </p>
        ) : null}
        {report.status === "failed" ? (
          <p className="text-[14px] text-destructive" role="status">
            Die Meldung ist nicht angekommen. Lade die Anwendung neu und
            versuch es noch einmal, oder sag deiner Ansprechperson Bescheid.
            Nenn dabei die Uhrzeit und was du zuletzt gemacht hast.
          </p>
        ) : null}
      </div>
    </div>
  )
}
